import { Link } from "@tanstack/react-router";
import { Clock, Tag } from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";
import { Faq } from "@/components/Faq";
import { SalonCard } from "@/components/SalonCard";
import { salons } from "@/data/salons";
import { SALON_BARBERARE } from "@/data/barbers";
import { BarberGrid } from "@/components/BarberGrid";
import { services } from "@/data/services";
import { serviceSectionsBySlug, serviceDescriptionBySlug } from "@/data/services.body";
import { track } from "@/lib/analytics";
import { GENERIC_BOKADIREKT, formatPriceFrom, withBookingUtm } from "@/lib/booking";
import type { Service } from "@/data/types";
import { internalPath } from "@/lib/paths";
import { LocalSalonLinks } from "@/components/LocalSalonLinks";
import { ContextLinkBlock } from "@/components/ContextLinkBlock";
import { SERVICE_SALON_LINKS, LOCAL_SERVICE_MORE } from "@/data/localServiceLinks";

import { ServiceHero } from "@/components/ServiceHero";

export function ServicePageTemplate({ service }: { service: Service }) {
  const offered = salons.filter((s) => service.availableAt.includes(s.slug));
  const related = service.relatedServices
    .map((slug) => services.find((s) => s.slug === slug))
    .filter((s): s is Service => Boolean(s));

  const salonBarberData = service.barberSalon ? SALON_BARBERARE[service.barberSalon] : undefined;

  return (
    <SiteShell>
      <ServiceHero
        breadcrumbName={service.name}
        eyebrow={service.category}
        title={service.name}
        directAnswerHeading={service.directAnswerHeading}
        directAnswer={service.directAnswer}
        intro={service.excerpt}
        priceLabel={formatPriceFrom(service.priceFrom)}
        durationLabel={service.durationMin ? `${service.durationMin} min` : undefined}
        ctaLabel={service.ctaLabel ?? "Välj salong & boka"}
        ctaHref={service.bookingUrl ?? GENERIC_BOKADIREKT}
        onCtaClick={() =>
          track("service_booking_click", {
            service_name: service.name,
            cta_location: "service_hero",
          })
        }
        image={service.image}
        imageAlt={`${service.name} på Tailor's Barber i Stockholm`}
        eager
      />


      {/* Description */}
      <section className="container-page py-12">
        <div className="grid md:grid-cols-[1fr_2fr] gap-10">
          <div>
            <div className="eyebrow">Om behandlingen</div>
            <h2 className="mt-3 text-3xl">Vad ingår?</h2>
          </div>
          <div className="space-y-4 text-lg leading-relaxed text-foreground/85">
            {(serviceDescriptionBySlug[service.slug] ?? []).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Body image */}
      {service.bodyImage && (
        <section className="container-page py-8">
          <div className="aspect-[16/9] md:aspect-[21/9] rounded-2xl overflow-hidden bg-muted shadow-soft">
            <img
              src={service.bodyImage}
              alt={service.bodyImageAlt ?? service.name}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
        </section>
      )}

      {/* Rich content sections */}
      {(serviceSectionsBySlug[service.slug]?.length ?? 0) > 0 && (
        <section className="container-page py-8 md:py-12">
          <div className="grid gap-10 md:gap-14">
            {serviceSectionsBySlug[service.slug]!.map((sec, i) => (
              <div key={i} className="grid md:grid-cols-[1fr_2fr] gap-6 md:gap-10">
                <div>
                  <h2 className="text-2xl md:text-3xl">{sec.heading}</h2>
                </div>
                <div className="space-y-4 text-foreground/85">
                  {sec.intro && <p className="text-lg leading-relaxed">{sec.intro}</p>}
                  {sec.items && sec.items.length > 0 && (
                    <ul className="space-y-3">
                      {sec.items.map((it, j) => (
                        <li key={j} className="leading-relaxed">
                          {it.title && (
                            <span className="font-semibold text-foreground">{it.title}</span>
                          )}
                          {it.title && " — "}
                          <span>{it.text}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Available salons */}
      <section className="container-page py-16">
        <div className="max-w-2xl mb-8">
          <div className="eyebrow">Bokning</div>
          <h2 className="mt-3 text-3xl md:text-4xl">Salonger som erbjuder {service.shortName.toLowerCase()}</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {offered.map((s) => (
            <SalonCard key={s.slug} salon={s} />
          ))}
        </div>
      </section>

      {/* Barbers */}
      <section className="container-page py-16">
        <div className="max-w-2xl mb-8">
          <h2 className="text-3xl md:text-4xl">
            {salonBarberData ? `Barberare på ${salonBarberData.name}` : "Våra barberare"}
          </h2>
        </div>
        {salonBarberData ? (
          <BarberGrid barbers={salonBarberData.barbers} salonName={salonBarberData.name} />
        ) : (
          <div className="card-elev p-8 md:p-10">
            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="eyebrow">Se alla barberare</div>
                <h3 className="mt-2 text-2xl md:text-3xl">Välj salong och boka</h3>
                <p className="mt-2 text-foreground/75 max-w-2xl">
                  Hitta en erfaren barberare som erbjuder {service.shortName.toLowerCase()} på den salong
                  som passar dig bäst.
                </p>
              </div>
              <Link to="/" className="btn-brass whitespace-nowrap">
                Hitta barberare →
              </Link>
            </div>
          </div>
        )}
      </section>

      {/* Related services */}
      {related.length > 0 && (
        <section className="container-page py-16">
          <h2 className="text-3xl mb-8">Relaterade behandlingar</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to={internalPath(r.url)} className="card-elev p-6 flex items-center justify-between">
                <div>
                  <div className="eyebrow">{r.category}</div>
                  <h3 className="mt-2 text-xl">{r.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{formatPriceFrom(r.priceFrom)}</p>
                </div>
                <span className="text-brass-deep">→</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {LOCAL_SERVICE_MORE[service.slug] && (
        <ContextLinkBlock
          heading={LOCAL_SERVICE_MORE[service.slug]!.heading}
          links={LOCAL_SERVICE_MORE[service.slug]!.links}
        />
      )}

      {SERVICE_SALON_LINKS[service.slug] && (
        <ContextLinkBlock
          heading={SERVICE_SALON_LINKS[service.slug]!.heading}
          intro={SERVICE_SALON_LINKS[service.slug]!.intro}
          links={SERVICE_SALON_LINKS[service.slug]!.links}
          footer={
            service.slug === "skaggtrimning" ? (
              <p>
                Vill du ha ett helt rent ansikte i stället för trimmat skägg? Läs om{" "}
                <Link to={internalPath("/slatrakning/")} className="underline font-medium">
                  slätrakning med rakkniv
                </Link>
                .
              </p>
            ) : undefined
          }
        />
      )}

      {service.slug === "herrklippning-skaggtrimning" && (
        <section className="container-page py-10">
          <p className="text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
            Vill du i stället ha ansiktet helt rent kan du boka{" "}
            <Link to={internalPath("/slatrakning/")} className="underline font-medium">
              slätrakning med rakkniv
            </Link>{" "}
            som egen behandling.
          </p>
        </section>
      )}

      <LocalSalonLinks service={service} />
      
      

      <Faq items={service.faq} heading={`Vanliga frågor om ${service.shortName.toLowerCase()}`} />
    </SiteShell>
  );
}
